<script lang="ts">
  import { Link, navigate } from 'svelte-routing';
  import { api } from '../../lib/api/client';
  import { isAuthenticated } from '../../lib/stores/auth';
  import PageHeader from '../../lib/components/PageHeader.svelte';
  import DayView from '../../lib/components/health/DayView.svelte';
  import PeriodView from '../../lib/components/health/PeriodView.svelte';
  import { calendar, today } from '../../lib/utils/insights';
  import { weekStart } from '../../lib/utils/progress';
  import { hasNext, periodLabel, shiftPeriod, type HealthDay, type HealthPeriod, type HealthTab } from '../../lib/utils/health';
  import { t } from '../../lib/i18n/index.ts';

  // Health is the member's own health history (spec 007 FR-018): Day,
  // Week, Month, and Year tabs, arrows to step between periods, and each
  // period's cards.

  $effect(() => {
    if (!$isAuthenticated) navigate('/login', { replace: true });
  });

  const TABS: HealthTab[] = ['day', 'week', 'month', 'year'];
  const ws = weekStart(navigator.language);

  // A link may open a day, as the year calendar on Progress does.
  const asked = new URLSearchParams(window.location.search).get('date');
  let tab = $state<HealthTab>('day');
  let date = $state(asked && /^\d{4}-\d{2}-\d{2}$/.test(asked) && asked <= today() ? asked : today());
  let day = $state<HealthDay | null>(null);
  let period = $state<HealthPeriod | null>(null);
  let loading = $state(false);
  let error = $state('');
  // seq drops an answer that a later step has already replaced.
  let seq = 0;

  $effect(() => {
    const which = tab, d = date;
    const mine = ++seq;
    loading = true;
    const got = which === 'day'
      ? api.get<HealthDay>(`/me/health/day?date=${d}`).then((v) => { if (mine === seq) { day = v; period = null; } })
      : api.get<HealthPeriod>(`/me/health/period?period=${which}&date=${d}&${calendar()}`).then((v) => { if (mine === seq) { period = v; day = null; } });
    got.then(() => { if (mine === seq) error = ''; })
      .catch((e) => { if (mine === seq) error = e.message; })
      .finally(() => { if (mine === seq) loading = false; });
  });

  function step(n: number) {
    date = shiftPeriod(tab, date, n, ws);
  }

  let label = $derived(periodLabel(tab, date, ws));
  let next = $derived(hasNext(tab, date, ws));

  // GROUPS and ACCOUNT stand in the privacy note for its two links, so a
  // translation may put them anywhere in its sentences.
  const GROUPS = '\u0001';
  const ACCOUNT = '\u0002';
  let note = $derived(t('healthPrivacy', { groups: GROUPS, account: ACCOUNT }).split(/(\u0001|\u0002)/));
</script>

<div class="p-6 max-w-5xl w-full mx-auto space-y-4">
  <Link to="/activities" class="text-sm hover:opacity-80"><i aria-hidden="true" class="las la-arrow-left mr-1"></i>{t('healthMyActivities')}</Link>
  <PageHeader title={t('healthTitle')} />
  <div class="flex flex-wrap items-center justify-between gap-3">
    <div class="flex gap-1" role="group" aria-label={t('healthPeriod')}>
      {#each TABS as id (id)}
        <button class="btn btn-sm {tab === id ? 'btn-primary' : 'btn-secondary'}" aria-pressed={tab === id} onclick={() => (tab = id)}>{t('healthTab', { tab: id })}</button>
      {/each}
    </div>
    <div class="flex items-center gap-1 text-sm">
      <button class="btn btn-secondary btn-sm" aria-label={t('healthBefore', { tab })} onclick={() => step(-1)}><i aria-hidden="true" class="las la-chevron-left"></i></button>
      <span class="whitespace-nowrap px-2 font-medium min-w-32 text-center" aria-live="polite">{label}</span>
      <button class="btn btn-secondary btn-sm" aria-label={t('healthAfter', { tab })} disabled={!next} onclick={() => step(1)}><i aria-hidden="true" class="las la-chevron-right"></i></button>
    </div>
  </div>
  {#if error}
    <p class="text-sm" style="color: var(--theme-error);">{error}</p>
  {/if}
  <div class:opacity-60={loading} aria-busy={loading}>
    {#if tab === 'day' && day}
      <DayView {day} />
    {:else if tab !== 'day' && period}
      <PeriodView p={period} />
    {:else if loading}
      <p class="text-sm text-text-muted">{t('healthLoading')}</p>
    {/if}
  </div>
  <p class="text-xs text-text-muted">
    {#each note as part, k (k)}{#if part === GROUPS}<Link to="/groups">{t('healthGroupsLink')}</Link>{:else if part === ACCOUNT}<Link to="/settings/history">{t('healthAccountLink')}</Link>{:else}{part}{/if}{/each}
  </p>
</div>
